import RadioCardGroup, { type RadioCardOption } from 'Components/RadioCardGroup/RadioCardGroup'
import type { BulkSaleTicketProduct } from 'Services/SalesManagement/Features/TicketSale/types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useMemo } from 'react'
import { t } from 'ttag'

interface ProductSelection {
  productId: string
  ageGroupId: number
}

interface BulkTicketSaleProductSelectProps {
  salesChannelId: string | undefined
  allProducts: BulkSaleTicketProduct[]
  isLoading: boolean
  value: string
  onChange: (selection: ProductSelection) => void
  error?: string
}

export default function BulkTicketSaleProductSelect({
  salesChannelId,
  allProducts,
  isLoading,
  value,
  onChange,
  error
}: Readonly<BulkTicketSaleProductSelectProps>) {
  const options: RadioCardOption[] = useMemo(
    () =>
      allProducts.map(({ id, name, description }) => ({
        value: id,
        title: name,
        description
      })),
    [allProducts]
  )

  const handleChange = (productId: string) => {
    const product = allProducts.find(p => p.id === productId)
    if (product) {
      onChange({ productId, ageGroupId: product.ageGroupId })
    }
  }

  const selectedProductInfo = useMemo(
    () => allProducts.find(p => p.id === value)?.info ?? null,
    [allProducts, value]
  )

  if (!salesChannelId) {
    return (
      <Typography color="text.secondary">{t`Select an authority first to see available ticket types.`}</Typography>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <>
      {options.length ? (
        <>
          <RadioCardGroup value={value} onChange={handleChange} options={options} error={error} />
          {selectedProductInfo && (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 2,
                mt: 2,
                maxWidth: 1000
              }}
            >
              <InfoOutlinedIcon color="info" />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="subtitle1" fontWeight={500}>
                  {selectedProductInfo.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {selectedProductInfo.body}
                </Typography>
              </Box>
            </Box>
          )}
        </>
      ) : (
        <Typography color="text.secondary">{t`No ticket types found for the selected authority.`}</Typography>
      )}
    </>
  )
}
